import { HeaderSearchResults } from 'src/contracts/headerSearch';
import axios from 'axios';

import {
  CompanyDocumentRequirementData,
  CompanyDressCodeData,
  CompanyDressCodeInput,
  CompanyClientContactData,
  CompanyClientContactInput,
  CompanyClientData,
  CompanyClientDocumentData,
  CompanyClientDocumentInput,
  CompanyClientInput,
  CompanyClientLocationData,
  CompanyClientLocationInput,
  CompanyClientAgreementAssignmentData,
  CompanyClientAgreementInput,
  CompanyClientScopedAgreementVersionInput,
  CompanyClientScopedAgreementVersionResult,
  CompanyClientAgreementTemplateData,
  CompanyClientAgreementVersionData,
  CompanyApplicationDecision,
  CompanyOnboardingApplication,
  CompanyOnboardingDraftCredentials,
  CompanyInsurancePolicyData,
  CompanyInsurancePolicyInput,
  CompanyProfileData,
  CompanyDashboardData,
  CompanyDataTableData,
  CompanyProfileSummaryData,
  CompanyProfileUpdate,
  CompanyRequirementWorkforceData,
  CompanyWorkerContractInput,
  CompanyWorkerContractTemplateData,
  CompanyWorkerContractWorkforceData,
  CompanyWorkerDetailData,
  CompanyWorkerRosterData,
  CompanyAdminAccessLevel,
  CompanyAdminUsersData,
  CompanyEmailTemplateData,
  CompanyEmailTemplateInput,
  CompanyEmailTemplatePreviewData,
  CompanyEmailPreviewVariant,
  CompanyEmailTemplatesData,
  CompanyPositionData,
  CompanyPositionInput,
  CompanyPositionWorkspaceData,
  RateCalculationResultData,
  RatePolicyConfig,
  RateRuleAudience,
  RateRuleCatalogData,
  RateRuleSetData
} from 'src/contracts/companyProfile';
import { parseMultiCompanySuccessEnvelope } from 'src/contracts/multiCompany/v1';
import {
  companyTenantApiPath,
  TenantWorkspaceRouteScope
} from 'src/workspaces/tenantWorkspace';

export async function getCompanyProfile(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyProfileData> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'profile');
  const response = await axios.get(path);
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.company) {
    throw new Error('Company profile response is invalid');
  }
  return envelope.data.company as CompanyProfileData;
}

export async function updateCompanyProfile(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyProfileUpdate
): Promise<CompanyProfileData> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'profile');
  const response = await axios.put(path, input);
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.company) {
    throw new Error('Company profile response is invalid');
  }
  return envelope.data.company as CompanyProfileData;
}

export async function uploadCompanyProfileLogo(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  file: File
): Promise<CompanyProfileData> {
  const body = new FormData();
  body.append('file', file);
  const path = companyTenantApiPath(workspace, routeCompanyId, 'profile/logo');
  const response = await axios.post(path, body);
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.company) {
    throw new Error('Company logo response is invalid');
  }
  return envelope.data.company as CompanyProfileData;
}

export async function getCompanyProfileSummary(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  scope?: 'company' | 'mine'
): Promise<CompanyProfileSummaryData> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    'profile/summary'
  );
  const response = await axios.get(path, {
    params: scope ? { scope } : undefined
  });
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.summary) {
    throw new Error('Company profile summary response is invalid');
  }
  return envelope.data.summary as CompanyProfileSummaryData;
}

export async function getCompanyDashboard(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  scope?: 'company' | 'mine',
  calendarWindow?: { start: string; end: string }
): Promise<CompanyDashboardData> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    'profile/dashboard'
  );
  const response = await axios.get(path, {
    params: {
      ...(scope ? { scope } : {}),
      ...(calendarWindow
        ? {
            calendarStart: calendarWindow.start,
            calendarEnd: calendarWindow.end
          }
        : {})
    }
  });
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.dashboard) {
    throw new Error('Company dashboard response is invalid');
  }
  return envelope.data.dashboard as CompanyDashboardData;
}

export async function getCompanyDataTable(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  resource: string,
  search?: string,
  scope?: 'company' | 'mine'
): Promise<CompanyDataTableData> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/data/${resource}`
  );
  const response = await axios.get(path, {
    params: {
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(scope ? { scope } : {})
    }
  });
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.table) {
    throw new Error('Company table response is invalid');
  }
  return envelope.data.table as CompanyDataTableData;
}

const companyWorkerRosterPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(workspace, routeCompanyId, `profile/workers${suffix}`);

export type CompanyWorkerRosterFilter =
  | 'scheduled-on-show'
  | 'ready'
  | 'onboarding';

export async function getCompanyWorkerRoster(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  filter?: CompanyWorkerRosterFilter,
  scope?: 'company' | 'mine'
): Promise<CompanyWorkerRosterData> {
  const response = await axios.get(
    companyWorkerRosterPath(workspace, routeCompanyId, '/roster'),
    {
      params:
        filter || scope
          ? {
              ...(filter ? { filter } : {}),
              ...(scope ? { scope } : {})
            }
          : undefined
    }
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.roster) {
    throw new Error('Company Workers roster response is invalid');
  }
  return envelope.data.roster as CompanyWorkerRosterData;
}

export async function getCompanyWorkerDetail(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number
): Promise<CompanyWorkerDetailData> {
  if (!Number.isSafeInteger(workerId) || workerId <= 0) {
    throw new Error('Company worker ID is invalid');
  }
  const response = await axios.get(
    companyWorkerRosterPath(workspace, routeCompanyId, `/${workerId}`)
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (
    !envelope?.data?.worker ||
    envelope.data.worker.id !== workerId ||
    !Number.isSafeInteger(envelope.data.actorId)
  ) {
    throw new Error('Company worker response is invalid');
  }
  return envelope.data as CompanyWorkerDetailData;
}

export async function setCompanyWorkerFavorite(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  isFavorite: boolean
): Promise<void> {
  await axios.put(
    companyWorkerRosterPath(workspace, routeCompanyId, `/${workerId}/favorite`),
    { isFavorite }
  );
}

export async function setCompanyWorkerBlocked(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  isBlocked: boolean
): Promise<void> {
  await axios.put(
    companyWorkerRosterPath(workspace, routeCompanyId, `/${workerId}/block`),
    { isBlocked }
  );
}

export async function sendCompanyWorkerNotification(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: { title: string; data: string; recipients: number[] }
): Promise<void> {
  await axios.post(
    companyWorkerRosterPath(
      workspace,
      routeCompanyId,
      '/notifications/send-now'
    ),
    input
  );
}

export async function scheduleCompanyWorkerNotification(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: {
    title: string;
    data: string;
    recipients: number[];
    scheduledAt: string;
  }
): Promise<void> {
  await axios.post(
    companyWorkerRosterPath(
      workspace,
      routeCompanyId,
      '/notifications/schedule'
    ),
    input
  );
}

export async function getCompanyDocumentRequirements(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyDocumentRequirementData[]> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    'profile/document-requirements'
  );
  const response = await axios.get(path);
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function saveCompanyDocumentRequirement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  documentType: string,
  input: { required: boolean; notes: string | null; workforceGroups: string[] }
): Promise<CompanyDocumentRequirementData> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/document-requirements/${documentType}`
  );
  const response = await axios.put(path, input);
  return response.data.data as CompanyDocumentRequirementData;
}

export async function removeCompanyDocumentRequirement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  documentType: string
): Promise<void> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/document-requirements/${documentType}`
  );
  await axios.delete(path);
}

export async function getCompanyRequirementWorkforce(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  requirement: 'tax-forms' | 'worker-insurance',
  completion?: 'completed' | 'missing',
  search?: string
): Promise<CompanyRequirementWorkforceData> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    'profile/requirement-workforce'
  );
  const response = await axios.get(path, {
    params: {
      requirement,
      ...(completion ? { completion } : {}),
      ...(search?.trim() ? { search: search.trim() } : {})
    }
  });
  return response.data.data as CompanyRequirementWorkforceData;
}

export async function viewCompanyRequirementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  documentType: string | number
): Promise<Blob> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/requirement-workforce/${workerId}/documents/${encodeURIComponent(
      documentType
    )}/file`
  );
  const response = await axios.get(path, { responseType: 'blob' });
  return response.data as Blob;
}

export async function viewCompanyTaxFormRecordDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  documentId: number
): Promise<Blob> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/requirement-workforce/${workerId}/document-records/${documentId}/file`
  );
  const response = await axios.get(path, { responseType: 'blob' });
  return response.data as Blob;
}

export async function updateCompanyRequirementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  documentId: number,
  input: {
    expectedRevision: number;
    status: 'VERIFIED' | 'NEEDS_CORRECTION';
    correctionReason?: string;
    expiresAt?: string | null;
  }
): Promise<void> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/requirement-workforce/${workerId}/documents/${documentId}`
  );
  await axios.patch(path, input);
}

export async function removeCompanyRequirementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  workerId: number,
  documentId: number,
  expectedRevision: number
): Promise<void> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/requirement-workforce/${workerId}/documents/${documentId}`
  );
  await axios.delete(path, { params: { expectedRevision } });
}

const companyWorkerContractsPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/worker-contracts${suffix}`
  );

export async function getCompanyWorkerContracts(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyWorkerContractTemplateData[]> {
  const response = await axios.get(
    companyWorkerContractsPath(workspace, routeCompanyId)
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function getCompanyAgreementCapabilities(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  audience: 'worker' | 'client'
) {
  const response = await axios.get(
    audience === 'worker'
      ? companyWorkerContractsPath(workspace, routeCompanyId, '/capabilities')
      : companyClientAgreementsPath(workspace, routeCompanyId, '/capabilities')
  );
  return Number(response.data?.data?.agreementRulesVersion ?? 0) >= 2;
}

export async function createCompanyWorkerContract(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyWorkerContractInput
): Promise<CompanyWorkerContractTemplateData> {
  const response = await axios.post(
    companyWorkerContractsPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyWorkerContractTemplateData;
}

export async function uploadCompanyWorkerAgreementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  file: File
) {
  const body = new FormData();
  body.append('file', file);
  const response = await axios.post(
    companyWorkerContractsPath(workspace, routeCompanyId, '/upload'),
    body
  );
  const uploaded = response.data?.data;
  if (!uploaded?.filename || !uploaded?.originalname)
    throw new Error('Agreement upload response is invalid');
  return {
    documentFilename: String(uploaded.filename),
    documentOriginalFilename: String(uploaded.originalname),
    documentMimeType: file.type || 'application/octet-stream'
  };
}

export async function downloadCompanyWorkerAgreementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  versionId: number
): Promise<Blob> {
  const response = await axios.get(
    companyWorkerContractsPath(
      workspace,
      routeCompanyId,
      `/${templateId}/versions/${versionId}/document`
    ),
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export async function reviseCompanyWorkerContract(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  input: CompanyWorkerContractInput
): Promise<CompanyWorkerContractTemplateData> {
  const response = await axios.put(
    companyWorkerContractsPath(workspace, routeCompanyId, `/${templateId}`),
    input
  );
  return response.data.data as CompanyWorkerContractTemplateData;
}

export async function archiveCompanyWorkerContract(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number
): Promise<CompanyWorkerContractTemplateData> {
  const response = await axios.delete(
    companyWorkerContractsPath(workspace, routeCompanyId, `/${templateId}`)
  );
  return response.data.data as CompanyWorkerContractTemplateData;
}

export async function getCompanyWorkerContractWorkforce(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  completion?: 'completed' | 'missing',
  search?: string
): Promise<CompanyWorkerContractWorkforceData> {
  const response = await axios.get(
    companyWorkerContractsPath(workspace, routeCompanyId, '/workforce'),
    {
      params: {
        ...(completion ? { completion } : {}),
        ...(search?.trim() ? { search: search.trim() } : {})
      }
    }
  );
  return response.data.data as CompanyWorkerContractWorkforceData;
}

const companyAdminUsersPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/admin-users${suffix}`
  );

export async function getCompanyAdminUsers(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyAdminUsersData> {
  const response = await axios.get(
    companyAdminUsersPath(workspace, routeCompanyId)
  );
  return response.data.data as CompanyAdminUsersData;
}

export async function inviteCompanyAdminUser(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  email: string,
  accessLevel: CompanyAdminAccessLevel,
  customRoleId?: number | null
): Promise<void> {
  await axios.post(
    companyAdminUsersPath(workspace, routeCompanyId, '/invitations'),
    { email, accessLevel, customRoleId }
  );
}

export async function updateCompanyAdminUser(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  grantId: number,
  input: {
    accessLevel: CompanyAdminAccessLevel;
    status: string;
    customRoleId?: number | null;
  }
): Promise<void> {
  await axios.put(
    companyAdminUsersPath(workspace, routeCompanyId, `/${grantId}`),
    input
  );
}

export async function createCompanyAdminRole(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: {
    name: string;
    description?: string | null;
    permissionLevels: CompanyAdminAccessLevel[];
  }
): Promise<void> {
  await axios.post(
    companyAdminUsersPath(workspace, routeCompanyId, '/roles'),
    input
  );
}

export async function revokeCompanyAdminInvitation(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  invitationId: number
): Promise<void> {
  await axios.delete(
    companyAdminUsersPath(
      workspace,
      routeCompanyId,
      `/invitations/${invitationId}`
    )
  );
}

const companyEmailTemplatesPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/email-templates${suffix}`
  );

export async function getCompanyEmailTemplates(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyEmailTemplatesData> {
  const response = await axios.get(
    companyEmailTemplatesPath(workspace, routeCompanyId)
  );
  return response.data.data as CompanyEmailTemplatesData;
}

export async function createCompanyEmailTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyEmailTemplateInput
): Promise<CompanyEmailTemplateData> {
  const response = await axios.post(
    companyEmailTemplatesPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyEmailTemplateData;
}

export async function previewCompanyEmailTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: {
    variant: CompanyEmailPreviewVariant;
    subject: string | null | undefined;
    body: string;
    primaryColor?: string;
    secondaryColor?: string;
  }
): Promise<CompanyEmailTemplatePreviewData> {
  const response = await axios.post(
    companyEmailTemplatesPath(workspace, routeCompanyId, '/preview'),
    input
  );
  return response.data.data as CompanyEmailTemplatePreviewData;
}

export async function updateCompanyEmailTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  input: CompanyEmailTemplateInput
): Promise<CompanyEmailTemplateData> {
  const response = await axios.put(
    companyEmailTemplatesPath(workspace, routeCompanyId, `/${templateId}`),
    input
  );
  return response.data.data as CompanyEmailTemplateData;
}

export async function setDefaultCompanyEmailTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number
): Promise<CompanyEmailTemplateData> {
  const response = await axios.post(
    companyEmailTemplatesPath(
      workspace,
      routeCompanyId,
      `/${templateId}/default`
    )
  );
  return response.data.data as CompanyEmailTemplateData;
}

export async function removeCompanyEmailTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number
): Promise<void> {
  await axios.delete(
    companyEmailTemplatesPath(workspace, routeCompanyId, `/${templateId}`)
  );
}

const companyInsurancePath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/insurance-policies${suffix}`
  );

export async function getCompanyInsurancePolicies(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyInsurancePolicyData[]> {
  const response = await axios.get(
    companyInsurancePath(workspace, routeCompanyId)
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function uploadCompanyInsurancePolicyDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  file: File
) {
  const body = new FormData();
  body.append('file', file);
  const response = await axios.post(
    companyInsurancePath(workspace, routeCompanyId, '/upload'),
    body
  );
  const uploaded = response.data?.data;
  if (!uploaded?.filename || !uploaded?.originalname) {
    throw new Error('Policy document upload response is invalid');
  }
  return {
    filename: String(uploaded.filename),
    originalFilename: String(uploaded.originalname),
    mimeType: file.type || 'application/octet-stream'
  };
}

export async function createCompanyInsurancePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyInsurancePolicyInput
): Promise<CompanyInsurancePolicyData> {
  const response = await axios.post(
    companyInsurancePath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyInsurancePolicyData;
}

export async function updateCompanyInsurancePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  policyId: number,
  input: CompanyInsurancePolicyInput
): Promise<CompanyInsurancePolicyData> {
  const response = await axios.put(
    companyInsurancePath(workspace, routeCompanyId, `/${policyId}`),
    input
  );
  return response.data.data as CompanyInsurancePolicyData;
}

export async function removeCompanyInsurancePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  policyId: number
): Promise<void> {
  await axios.delete(
    companyInsurancePath(workspace, routeCompanyId, `/${policyId}`)
  );
}

export async function downloadCompanyInsurancePolicyDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  policyId: number
): Promise<Blob> {
  const response = await axios.get(
    companyInsurancePath(workspace, routeCompanyId, `/${policyId}/download`),
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export async function viewCompanyInsurancePolicyDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  policyId: number
): Promise<Blob> {
  const response = await axios.get(
    companyInsurancePath(workspace, routeCompanyId, `/${policyId}/view`),
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

const companyDressCodesPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/dress-codes${suffix}`
  );

export async function getCompanyDressCodes(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyDressCodeData[]> {
  const response = await axios.get(
    companyDressCodesPath(workspace, routeCompanyId)
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function createCompanyDressCode(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyDressCodeInput
): Promise<CompanyDressCodeData> {
  const response = await axios.post(
    companyDressCodesPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyDressCodeData;
}

export async function updateCompanyDressCode(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  dressCodeId: number,
  input: CompanyDressCodeInput
): Promise<CompanyDressCodeData> {
  const response = await axios.put(
    companyDressCodesPath(workspace, routeCompanyId, `/${dressCodeId}`),
    input
  );
  return response.data.data as CompanyDressCodeData;
}

export async function removeCompanyDressCode(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  dressCodeId: number
): Promise<void> {
  await axios.delete(
    companyDressCodesPath(workspace, routeCompanyId, `/${dressCodeId}`)
  );
}

const companyPositionsPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(workspace, routeCompanyId, `profile/positions${suffix}`);

export async function getCompanyPositionWorkspace(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyPositionWorkspaceData> {
  const response = await axios.get(
    companyPositionsPath(workspace, routeCompanyId)
  );
  return response.data.data as CompanyPositionWorkspaceData;
}

export async function createCompanyPosition(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyPositionInput
): Promise<CompanyPositionData> {
  const response = await axios.post(
    companyPositionsPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyPositionData;
}

export async function updateCompanyPosition(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  positionId: number,
  input: CompanyPositionInput
): Promise<CompanyPositionData> {
  const response = await axios.put(
    companyPositionsPath(workspace, routeCompanyId, `/${positionId}`),
    input
  );
  return response.data.data as CompanyPositionData;
}

export async function removeCompanyPosition(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  positionId: number
): Promise<void> {
  await axios.delete(
    companyPositionsPath(workspace, routeCompanyId, `/${positionId}`)
  );
}

const companyClientsPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(workspace, routeCompanyId, `profile/clients${suffix}`);

export async function getCompanyClients(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  search?: string
): Promise<CompanyClientData[]> {
  const response = await axios.get(
    companyClientsPath(workspace, routeCompanyId),
    { params: search?.trim() ? { search: search.trim() } : undefined }
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function getCompanyClient(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string
): Promise<CompanyClientData> {
  const response = await axios.get(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}`
    )
  );
  if (!response.data?.data?.id) {
    throw new Error('Client Profile response is invalid');
  }
  return response.data.data as CompanyClientData;
}

export async function createCompanyClient(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyClientInput
): Promise<CompanyClientData> {
  const response = await axios.post(
    companyClientsPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyClientData;
}

export async function getCompanyQuoteCreationCapabilities(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<{ idempotentDraftCreation?: boolean }> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'events/creation-capabilities');
  return (await axios.get(path)).data?.data;
}

export async function createCompanyQuote(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: Record<string, unknown>,
  requestKey?: string
): Promise<any> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'events');
  const response = requestKey
    ? await axios.post(path, input, { headers: { 'Idempotency-Key': requestKey } })
    : await axios.post(path, input);
  return response;
}

export async function updateCompanyQuote(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  eventId: number,
  input: Record<string, unknown>
): Promise<any> {
  const response = await axios.put(
    companyTenantApiPath(workspace, routeCompanyId, `events/${eventId}`),
    input
  );
  return response;
}

export async function updateCompanyClient(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: Partial<CompanyClientInput>
): Promise<CompanyClientData> {
  const response = await axios.put(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}`
    ),
    input
  );
  return response.data.data as CompanyClientData;
}

export async function removeCompanyClient(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string
): Promise<void> {
  await axios.delete(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}`
    )
  );
}

export async function createCompanyClientContact(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: CompanyClientContactInput
): Promise<CompanyClientContactData> {
  const response = await axios.post(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/contacts`
    ),
    input
  );
  return response.data.data as CompanyClientContactData;
}

export async function updateCompanyClientContact(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  contactId: number,
  input: CompanyClientContactInput
): Promise<CompanyClientContactData> {
  const response = await axios.put(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/contacts/${contactId}`
    ),
    input
  );
  return response.data.data as CompanyClientContactData;
}

export async function removeCompanyClientContact(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  contactId: number
): Promise<void> {
  await axios.delete(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/contacts/${contactId}`
    )
  );
}

export async function createCompanyClientLocation(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: CompanyClientLocationInput
): Promise<CompanyClientLocationData> {
  const response = await axios.post(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/locations`
    ),
    input
  );
  return response.data.data as CompanyClientLocationData;
}

export async function updateCompanyClientLocation(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  locationId: number,
  input: CompanyClientLocationInput
): Promise<CompanyClientLocationData> {
  const response = await axios.put(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/locations/${locationId}`
    ),
    input
  );
  return response.data.data as CompanyClientLocationData;
}

export async function removeCompanyClientLocation(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  locationId: number
): Promise<void> {
  await axios.delete(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/locations/${locationId}`
    )
  );
}

export async function uploadCompanyClientDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  file: File
): Promise<{ filename: string; originalname: string; size: number }> {
  const body = new FormData();
  body.append('file', file);
  const response = await axios.post(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/documents/upload`
    ),
    body
  );
  return response.data.data;
}

export async function createCompanyClientDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: CompanyClientDocumentInput
): Promise<CompanyClientDocumentData> {
  const response = await axios.post(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/documents`
    ),
    input
  );
  return response.data.data as CompanyClientDocumentData;
}

export async function downloadCompanyClientDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  documentId: number
): Promise<Blob> {
  const response = await axios.get(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/documents/${documentId}/download`
    ),
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export async function removeCompanyClientDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  documentId: number
): Promise<void> {
  await axios.delete(
    companyClientsPath(
      workspace,
      routeCompanyId,
      `/${encodeURIComponent(clientKey)}/documents/${documentId}`
    )
  );
}

const companyClientAgreementsPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/client-agreements${suffix}`
  );

export async function getCompanyClientAgreements(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<CompanyClientAgreementTemplateData[]> {
  const response = await axios.get(
    companyClientAgreementsPath(workspace, routeCompanyId)
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function getCompanyClientAgreementVersion(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  versionId: number
): Promise<CompanyClientAgreementVersionData> {
  const response = await axios.get(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/${templateId}/versions/${versionId}`
    )
  );
  return response.data.data as CompanyClientAgreementVersionData;
}

export async function publishCompanyClientAgreementForQuote(
  workspace: TenantWorkspaceRouteScope,
  templateId: number,
  versionId: number,
  clientId: number,
  eventId: number
): Promise<CompanyClientAgreementVersionData> {
  const response = await axios.post(
    companyClientAgreementsPath(
      workspace,
      workspace.company.id,
      `/${templateId}/versions/${versionId}/publish-for-quote`
    ),
    { clientId, eventId }
  );
  return response.data.data as CompanyClientAgreementVersionData;
}

export async function createCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: CompanyClientAgreementInput
): Promise<CompanyClientAgreementTemplateData> {
  const response = await axios.post(
    companyClientAgreementsPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as CompanyClientAgreementTemplateData;
}

export async function createCompanyClientBillingTemplate(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: {
    name: string;
    config: RatePolicyConfig;
    sourceRuleSetVersionId?: number;
  }
): Promise<RateRuleSetData> {
  const response = await axios.post(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      '/billing-templates'
    ),
    input
  );
  return response.data.data as RateRuleSetData;
}

export async function uploadCompanyClientAgreementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  file: File
): Promise<{
  filename: string;
  originalFilename: string;
  mimeType: string;
}> {
  const body = new FormData();
  body.append('file', file);
  const response = await axios.post(
    companyClientAgreementsPath(workspace, routeCompanyId, '/upload'),
    body
  );
  const uploaded = response.data?.data;
  if (!uploaded?.filename || !uploaded?.originalname) {
    throw new Error('Agreement document upload response is invalid');
  }
  return {
    filename: String(uploaded.filename),
    originalFilename: String(uploaded.originalname),
    mimeType: file.type || 'application/octet-stream'
  };
}

export async function downloadCompanyClientAgreementDocument(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  versionId: number
): Promise<Blob> {
  const response = await axios.get(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/${templateId}/versions/${versionId}/document`
    ),
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export async function reviseCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number,
  input: CompanyClientAgreementInput
): Promise<CompanyClientAgreementTemplateData> {
  const response = await axios.put(
    companyClientAgreementsPath(workspace, routeCompanyId, `/${templateId}`),
    input
  );
  return response.data.data as CompanyClientAgreementTemplateData;
}

export async function setDefaultCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number
): Promise<CompanyClientAgreementTemplateData> {
  const response = await axios.post(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/${templateId}/default`
    )
  );
  return response.data.data as CompanyClientAgreementTemplateData;
}

export async function removeCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  templateId: number
): Promise<void> {
  await axios.delete(
    companyClientAgreementsPath(workspace, routeCompanyId, `/${templateId}`)
  );
}

export async function assignCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: {
    templateId: number;
    versionId?: number;
    inherited: boolean;
    effectiveAt?: string;
  }
): Promise<CompanyClientAgreementAssignmentData> {
  const response = await axios.put(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/clients/${encodeURIComponent(clientKey)}/assignment`
    ),
    input
  );
  return response.data.data as CompanyClientAgreementAssignmentData;
}

export async function createCompanyClientScopedAgreementVersion(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: CompanyClientScopedAgreementVersionInput
): Promise<CompanyClientAgreementAssignmentData> {
  const response = await axios.post(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/clients/${encodeURIComponent(clientKey)}/versions`
    ),
    input
  );
  return response.data.data as CompanyClientAgreementAssignmentData;
}

export async function saveCompanyClientScopedAgreementVersion(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string,
  input: CompanyClientScopedAgreementVersionInput
): Promise<CompanyClientScopedAgreementVersionResult> {
  const response = await axios.put(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/clients/${encodeURIComponent(clientKey)}/versions`
    ),
    input
  );
  return response.data.data as CompanyClientScopedAgreementVersionResult;
}

export async function clearCompanyClientAgreement(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  clientKey: string
): Promise<void> {
  await axios.delete(
    companyClientAgreementsPath(
      workspace,
      routeCompanyId,
      `/clients/${encodeURIComponent(clientKey)}/assignment`
    )
  );
}

const companyRateRuleSetsPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  suffix = ''
) =>
  companyTenantApiPath(
    workspace,
    routeCompanyId,
    `profile/rate-rule-sets${suffix}`
  );

export async function getCompanyRateRuleCatalog(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): Promise<RateRuleCatalogData> {
  const response = await axios.get(
    companyRateRuleSetsPath(workspace, routeCompanyId, '/catalog')
  );
  return response.data.data as RateRuleCatalogData;
}

export async function getCompanyRateRuleSets(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  audience?: RateRuleAudience
): Promise<RateRuleSetData[]> {
  const response = await axios.get(
    companyRateRuleSetsPath(workspace, routeCompanyId),
    { params: audience ? { audience } : undefined }
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function createCompanyRateRuleSet(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  input: {
    audience: RateRuleAudience;
    label: string;
    description?: string | null;
    config: RatePolicyConfig;
    effectiveAt?: string | null;
  }
): Promise<RateRuleSetData> {
  const response = await axios.post(
    companyRateRuleSetsPath(workspace, routeCompanyId),
    input
  );
  return response.data.data as RateRuleSetData;
}

export async function saveCompanyRateRuleSetDraft(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  ruleSetId: number,
  input: {
    label: string;
    description?: string | null;
    config: RatePolicyConfig;
    effectiveAt?: string | null;
  }
): Promise<RateRuleSetData> {
  const response = await axios.put(
    companyRateRuleSetsPath(workspace, routeCompanyId, `/${ruleSetId}/draft`),
    input
  );
  return response.data.data as RateRuleSetData;
}

export async function publishCompanyRateRuleSet(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  ruleSetId: number
): Promise<RateRuleSetData> {
  const response = await axios.post(
    companyRateRuleSetsPath(workspace, routeCompanyId, `/${ruleSetId}/publish`)
  );
  return response.data.data as RateRuleSetData;
}

export async function archiveCompanyRateRuleSet(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  ruleSetId: number
): Promise<RateRuleSetData> {
  const response = await axios.post(
    companyRateRuleSetsPath(workspace, routeCompanyId, `/${ruleSetId}/archive`)
  );
  return response.data.data as RateRuleSetData;
}

export async function validateCompanyRatePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  config: RatePolicyConfig
): Promise<{ valid: true; config: RatePolicyConfig }> {
  const response = await axios.post(
    companyRateRuleSetsPath(workspace, routeCompanyId, '/validate'),
    { config }
  );
  return response.data.data as { valid: true; config: RatePolicyConfig };
}

export async function previewCompanyRatePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  config: RatePolicyConfig,
  scenario: Record<string, unknown>
): Promise<RateCalculationResultData> {
  const response = await axios.post(
    companyRateRuleSetsPath(workspace, routeCompanyId, '/preview-calculation'),
    { config, scenario }
  );
  return response.data.data as RateCalculationResultData;
}

export async function calculateCompanyRatePolicy(
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  ruleSetId: number,
  versionId: number,
  scenario: Record<string, unknown>
): Promise<RateCalculationResultData> {
  const response = await axios.post(
    companyRateRuleSetsPath(
      workspace,
      routeCompanyId,
      `/${ruleSetId}/versions/${versionId}/calculate`
    ),
    scenario
  );
  return response.data.data as RateCalculationResultData;
}

const applicationHeaders = (resumeToken: string) => ({
  headers: { 'X-Application-Token': resumeToken }
});

export async function createCompanyApplication(ownerEmail: string): Promise<{
  credentials: CompanyOnboardingDraftCredentials;
  application: CompanyOnboardingApplication;
}> {
  const response = await axios.post('/company-onboarding/applications', {
    ownerEmail
  });
  const payload = response.data?.data;
  if (!payload?.resumeToken || !payload?.application?.id) {
    throw new Error('Company application response is invalid');
  }
  return {
    credentials: {
      applicationId: payload.application.id,
      resumeToken: payload.resumeToken
    },
    application: payload.application as CompanyOnboardingApplication
  };
}

/**
 * Asks the API to email a fresh resume link. The API always answers success
 * so the call cannot reveal whether an application exists for the email.
 */
export async function requestCompanyApplicationResumeLink(
  ownerEmail: string
): Promise<void> {
  await axios.post('/company-onboarding/applications/resume-link', {
    ownerEmail
  });
}

export async function getCompanyApplication(
  credentials: CompanyOnboardingDraftCredentials
): Promise<CompanyOnboardingApplication> {
  const response = await axios.get(
    `/company-onboarding/applications/${encodeURIComponent(
      credentials.applicationId
    )}`,
    applicationHeaders(credentials.resumeToken)
  );
  return response.data.data as CompanyOnboardingApplication;
}

export async function updateCompanyApplication(
  credentials: CompanyOnboardingDraftCredentials,
  input: Record<string, unknown>
): Promise<CompanyOnboardingApplication> {
  const response = await axios.patch(
    `/company-onboarding/applications/${encodeURIComponent(
      credentials.applicationId
    )}`,
    input,
    applicationHeaders(credentials.resumeToken)
  );
  return response.data.data as CompanyOnboardingApplication;
}

export async function submitCompanyApplication(
  credentials: CompanyOnboardingDraftCredentials,
  expectedVersion: number
): Promise<CompanyOnboardingApplication> {
  const response = await axios.post(
    `/company-onboarding/applications/${encodeURIComponent(
      credentials.applicationId
    )}/submit`,
    { expectedVersion },
    applicationHeaders(credentials.resumeToken)
  );
  return response.data.data as CompanyOnboardingApplication;
}

export async function getPlatformCompanyApplications(
  status?: string
): Promise<CompanyOnboardingApplication[]> {
  const response = await axios.get('/platform/company-applications', {
    params: status ? { status } : undefined
  });
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  return Array.isArray(envelope?.data?.items) ? envelope!.data.items : [];
}

export async function decidePlatformCompanyApplication(
  applicationId: string,
  decision: CompanyApplicationDecision,
  notes?: string
): Promise<CompanyOnboardingApplication> {
  const response = await axios.post(
    `/platform/company-applications/${encodeURIComponent(
      applicationId
    )}/decision`,
    {
      expectedStatus: 'submitted',
      decision,
      ...(notes?.trim() ? { notes: notes.trim() } : {})
    }
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope?.data?.application) {
    throw new Error('Company application decision response is invalid');
  }
  return envelope.data.application as CompanyOnboardingApplication;
}

export async function resendCompanyAdminInvitation(
  workspace: TenantWorkspaceRouteScope, routeCompanyId: unknown,
  invitationId: number, expectedAttemptId: string | null
): Promise<void> {
  await axios.post(companyAdminUsersPath(workspace, routeCompanyId, `/invitations/${invitationId}/resend`), { expectedAttemptId });
}

export async function searchCompanyRecords(
  workspace: TenantWorkspaceRouteScope,
  search: string
): Promise<HeaderSearchResults> {
  const response = await axios.get(
    companyTenantApiPath(workspace, workspace.company.id, 'profile/search'),
    { params: { search } }
  );
  const envelope = parseMultiCompanySuccessEnvelope<{
    results: HeaderSearchResults;
  }>(response.data);
  if (!envelope?.data?.results) throw new Error('Workspace search response is invalid');
  return envelope.data.results;
}
